"use client";

import { PlayCircle, Video, Users, LucideIcon, Star } from 'lucide-react';

interface Feature {
  icon?: LucideIcon;
  text: string;
  color: string;
}

interface Course {
  badge: string;
  badgeColor: string;
  image: string;
  title: string;
  description: string;
  features: Feature[];
  price: string;
  rating?: number;
  buttonText?: string;
  buttonColor?: string;
}

const CourseMasterPage = () => {
  const steps = [
    {
      number: 1,
      title: "Nhận đàn",
      description: "Đàn được xưởng cân chỉnh chính xác, tặng kèm đầy đủ phụ kiện giao đến tay bạn"
    },
    {
      number: 2,
      title: "Gửi mã đơn",
      description: "Liên hệ qua Zalo hoặc Facebook của xưởng, gửi kiểm mã đơn hàng để được xác thực"
    },
    {
      number: 3,
      title: "Vào nhóm lớp",
      description: "Nhận link tham gia nhóm kín Zalo/Facebook, nhận giáo trình và bắt đầu bài học đầu tiên"
    }
  ];

  const courses: Course[] = [
    {
      badge: "QUÀ TẶNG TRỌN ĐỜI",
      badgeColor: "bg-red-600",
      image: "https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=800&q=80",
      title: "30 Ngày Đệm Hát Cùng Sài Thành",
      description: "Lộ trình được thiết kế riêng cho người chưa biết nhạc lý. Tặng kèm giáo trình '30 ngày đệm hát' sẵn mPDF",
      features: [
        { icon: PlayCircle, text: "Video bài giảng chất lượng cao", color: "text-red-500" },
        { icon: Video, text: "Livestream trực tiếp định kỳ", color: "text-red-500" },
        { icon: Users, text: "Nhóm kín Zalo/Facebook hỗ trợ 24/7", color: "text-red-500" }
      ],
      price: "MIỄN PHÍ",
      rating: 5,
      buttonText: "",
      buttonColor: ""
    },
    {
      badge: "NÂNG CAO",
      badgeColor: "bg-gray-900",
      image: "https://images.unsplash.com/photo-1525201548942-d8732f6617a0?w=800&q=80",
      title: "Kỹ Thuật Fingerstyle Hiện Đại",
      description: "Dành cho các bạn đã nắm vững cơ bản, muốn học cách gõ thành và đệc tấu chuyên nghiệp",
      features: [
        { text: "video_library 45 bài giảng", color: "text-gray-600" },
        { text: "verified Chứng nhận", color: "text-gray-600" }
      ],
      price: "2.500.000đ",
      buttonText: "Đăng ký",
      buttonColor: "bg-red-600 hover:bg-red-700 text-white"
    },
    {
      badge: "VIP ONLINE",
      badgeColor: "bg-red-600",
      image: "https://images.unsplash.com/photo-1516924962500-2b4b3b99ea02?w=800&q=80",
      title: "Kèm Cấp Tốc 1-1 Online",
      description: "Thầy giáo kèm cặp trực tiếp qua Video Call, chỉnh sửa lỗi sai ngay tho tức cho bạn",
      features: [
        { text: "person Riêng tư", color: "text-gray-600" },
        { text: "event Linh hoạt", color: "text-gray-600" }
      ],
      price: "Liên hệ",
      buttonText: "Tư vấn",
      buttonColor: "border-2 border-red-600 text-red-600 hover:bg-red-50"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Quy trình 3 bước kích hoạt đơn giản
          </h1>
          <p className="text-gray-600 text-sm md:text-base">
            Bắt đầu hành trình chính phục Guitar chỉ trong vài phút
          </p>
        </div>
      </div>

      {/* Steps Section */}
      <div className="max-w-6xl mx-auto px-4 -mt-8 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((step) => (
            <div key={step.number} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                  <span className="text-white text-2xl font-bold">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Courses Section */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="mb-8">
          <p className="text-red-600 text-sm font-semibold uppercase tracking-wide mb-2">
            ĐẶC QUYỀN HỌC VIÊN
          </p>
          <h2 className="text-3xl font-bold text-gray-900">
            Nội dung khóa học trọn đời
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {courses.map((course, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="relative">
                <img 
                  src={course.image} 
                  alt={course.title}
                  className="w-full h-48 object-cover"
                />
                <div className={`absolute top-4 left-4 ${course.badgeColor} text-white px-4 py-1 rounded-full text-xs font-semibold`}>
                  {course.badge}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {course.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {course.description}
                </p>

                <div className="space-y-2 mb-6">
                  {course.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      {feature.icon ? (
                        <>
                          <feature.icon className={`w-4 h-4 mt-0.5 ${feature.color}`} />
                          <span className="text-sm text-gray-700">{feature.text}</span>
                        </>
                      ) : (
                        <span className={`text-sm ${feature.color}`}>{feature.text}</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 flex items-center justify-between">
                  <div>
                    <p className={`font-bold ${course.price === "MIỄN PHÍ" ? "text-red-600 text-lg" : "text-gray-900 text-xl"}`}>
                      {course.price}
                    </p>

                 
                  </div>
                  {course.buttonText && (
                    <button className={`px-6 py-2 rounded-lg font-semibold transition-all ${course.buttonColor}`}>
                      {course.buttonText}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Chat Button */}
    
    </div>
  );
};

export default CourseMasterPage;